import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { StarDisplay } from 'components/StarDisplay';
import { EmptyState } from 'components/EmptyState';
import { Colors } from 'constants/ui';
import { GlobalScrollLock } from 'components/GlobalScrollLock';

import { groupReviewsBySibling } from '../helpers';
import * as styled from '../styles/reviews';
import { InquiryModal } from './inquiry-modal';
import { ReviewCardBody } from './review-card-body';

type Props = {
  reviews: Kudable.Review[];
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
  sort: Profile.ReviewSort;
  badge: Profile.ReviewBadgeFilter;
  search: string;
  accentColor: string;
  onSortChange: (sort: Profile.ReviewSort) => void;
  onBadgeChange: (badge: Profile.ReviewBadgeFilter) => void;
  onSearchChange: (query: string) => void;
};

const SORT_OPTIONS: { key: Profile.ReviewSort; label: string }[] = [
  { key: 'recent', label: 'Newest' },
  { key: 'oldest', label: 'Oldest' },
  { key: 'highest', label: 'Highest' },
  { key: 'lowest', label: 'Lowest' },
  { key: 'updated', label: 'Recently updated' },
];

const BADGE_OPTIONS: { key: Profile.ReviewBadgeFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'job_confirmed', label: '🛡 Job Confirmed' },
  { key: 'verified', label: '✓ Verified' },
  { key: 'unverified', label: 'Unverified' },
];

export const ReviewsList = ({
  reviews,
  hasMore,
  isLoading,
  onLoadMore,
  sort,
  badge,
  search,
  accentColor,
  onSortChange,
  onBadgeChange,
  onSearchChange,
}: Props) => {
  const hasFilters = search !== '' || badge !== 'all';
  const [lightbox, setLightbox] = useState<Profile.LightboxAsset | null>(null);
  const [inquiryTarget, setInquiryTarget] = useState<Kudable.Review | null>(null);

  return (
    <div>
      <styled.Legend>
        <styled.LegendTitle>How we verify reviews</styled.LegendTitle>
        <styled.LegendRow>
          <styled.Badge $bg={accentColor} $color={Colors.white}>
            🛡 Job Confirmed
          </styled.Badge>
          <styled.LegendText>
            Provider entered a 4-digit code from the client confirming the job
            was completed.
          </styled.LegendText>
        </styled.LegendRow>
        <styled.LegendRow>
          <styled.Badge
            $bg={Colors.successBgSoft}
            $color={Colors.successDarkest}
          >
            ✓ Booked & verified
          </styled.Badge>
          <styled.LegendText>
            Client booked this provider through Kudable. Job not yet confirmed
            complete.
          </styled.LegendText>
        </styled.LegendRow>
      </styled.Legend>

      <styled.FilterBar>
        <styled.FilterSearch
          placeholder="Search reviews..."
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
        />
        <styled.FilterSelect
          value={sort}
          onChange={(event) =>
            onSortChange(event.target.value as Profile.ReviewSort)
          }
        >
          {SORT_OPTIONS.map((sortOption) => (
            <option key={sortOption.key} value={sortOption.key}>
              {sortOption.label}
            </option>
          ))}
        </styled.FilterSelect>
      </styled.FilterBar>

      <styled.EditLinkRow>
        <Link to="/reviews/edit" style={{ textDecoration: 'none' }}>
          <styled.EditLink as="span" $accent={accentColor}>
            ✎ Edit a review you've left
          </styled.EditLink>
        </Link>
      </styled.EditLinkRow>

      <styled.ChipsRow>
        {BADGE_OPTIONS.map((badgeOption) => (
          <styled.Chip
            key={badgeOption.key}
            type="button"
            $selected={badge === badgeOption.key}
            $accent={accentColor}
            onClick={() => onBadgeChange(badgeOption.key)}
          >
            {badgeOption.label}
          </styled.Chip>
        ))}
      </styled.ChipsRow>

      {reviews.length === 0 ? (
        <EmptyState
          icon={hasFilters ? '🔎' : '📭'}
          title={
            hasFilters ? 'No reviews match your filters' : 'No reviews yet'
          }
          body={
            hasFilters
              ? 'Try clearing the search or switching filters.'
              : "Once clients leave reviews, they'll appear here."
          }
        />
      ) : null}

      <styled.List>
        {groupReviewsBySibling(reviews).map((entry) =>
          entry.kind === 'single' ? (
            <styled.Card key={entry.review.id}>
              <ReviewCardBody
                review={entry.review}
                accentColor={accentColor}
                onOpenLightbox={setLightbox}
                onAskInquiry={setInquiryTarget}
              />
            </styled.Card>
          ) : (
            <styled.Card
              key={`g:${entry.groupId}`}
              $group
              $accent={accentColor}
            >
              <styled.GroupBadgeRow>
                <styled.GroupBadge $accent={accentColor}>
                  TEAM REVIEW
                </styled.GroupBadge>
              </styled.GroupBadgeRow>
              <ReviewCardBody
                review={entry.siblings[0]}
                accentColor={accentColor}
                onOpenLightbox={setLightbox}
                onAskInquiry={setInquiryTarget}
                hideStars
              />
              <styled.ParticipantsLabel>
                Team members on this job
              </styled.ParticipantsLabel>
              <styled.ParticipantList>
                {entry.siblings.map((sibling) => (
                  <styled.ParticipantRow key={sibling.id}>
                    <styled.ParticipantInfo>
                      <styled.ParticipantName>
                        {sibling.participant_label || 'Team member'}
                      </styled.ParticipantName>
                      {sibling.participant_role ? (
                        <styled.ParticipantRole>
                          {sibling.participant_role}
                        </styled.ParticipantRole>
                      ) : null}
                    </styled.ParticipantInfo>
                    <StarDisplay value={sibling.overall_rating} size={14} />
                  </styled.ParticipantRow>
                ))}
              </styled.ParticipantList>
            </styled.Card>
          ),
        )}
      </styled.List>
      {hasMore && (
        <styled.LoadMore
          $accent={accentColor}
          onClick={onLoadMore}
          disabled={isLoading}
        >
          {isLoading ? 'Loading...' : 'Load more'}
        </styled.LoadMore>
      )}

      {lightbox && (
        <styled.LightboxOverlay onClick={() => setLightbox(null)}>
          <GlobalScrollLock />
          <styled.LightboxContent onClick={(event) => event.stopPropagation()}>
            <styled.LightboxClose onClick={() => setLightbox(null)}>
              ✕
            </styled.LightboxClose>
            <styled.LightboxMedia>
              {lightbox.kind === 'video' ? (
                <video src={lightbox.url} controls autoPlay />
              ) : (
                <img src={lightbox.url} alt="" />
              )}
            </styled.LightboxMedia>
          </styled.LightboxContent>
        </styled.LightboxOverlay>
      )}

      {inquiryTarget && (
        <InquiryModal
          review={inquiryTarget}
          accentColor={accentColor}
          onClose={() => setInquiryTarget(null)}
        />
      )}
    </div>
  );
};
